import { Controller } from "@hotwired/stimulus"

// Daycare calendar controller for multi-day selection
//
// Usage:
//   <div data-controller="daycare-calendar"
//        data-daycare-calendar-location-service-id-value="service_abc123"
//        data-daycare-calendar-price-value="45.00">
//
export default class extends Controller {
  static targets = [
    "calendar",        // The calendar grid container
    "monthLabel",      // Month/year display
    "prevButton",      // Previous month button
    "nextButton",      // Next month button
    "selectedCount",   // Count of selected days
    "selectedUnit",    // "day" or "days", after the count
    "totalPrice",      // Total price display
    "addButton",       // Add to order button
    "loading",         // Loading indicator
    "form",            // The order form
    "datesContainer",  // Container for date hidden inputs
    "petsContainer",   // Container for pet selection cards
    "signInFork",      // Sign in / continue as new customer, shown before the calendar
    "calendarBody",    // Everything below the fork, revealed once they have chosen
    "notice",          // The message slot above the buttons — held days, dropped days
    "petBreakdown",    // Per-pet day counts, shown when pets are not on the same days
    "legendItem",      // Legend entries for the rarer day states, shown only when the month has one
    "pickingFor",      // "Choosing days for: All / Biscuit / Spike", shown for two pets or more
    "petCard",         // Individual pet card
    "petCheckbox",     // Pet checkbox inputs
    "petCount",        // Pet count display container
    "selectedPetCount", // Number of selected pets text
    "quantityDisplay",  // Quantity number display (for non-logged-in users)
    "decrementButton",  // Decrease quantity button
    "incrementButton",  // Increase quantity button
    // Recurring schedule targets
    "recurringSection",       // Recurring section container
    "recurringCheckbox",      // Make recurring checkbox
    "recurringCard",          // The card container
    "recurringCheckIndicator", // Checkmark indicator circle
    "recurringOptions",       // Recurring options container
    "recurringFrequency",     // Frequency dropdown
    "recurringDays",          // Days container
    "recurringDayButton",     // Individual day buttons
    "recurringDaysHint",      // Hint text for days
    "recurringSummaryDays",   // The days named in full, inside the plain-English summary
    "recurringInviteDays",    // The same days, inside the question asked when unticked
    "summaryPets",            // Who the selected days are for, beside the day count
    "recurringInvite",        // The question, shown only while unticked
    "recurringLeadIn",        // Its replacement once ticked, introducing the confirmation
    "recurringExplainer",     // What a schedule commits you to, shown before the decision
    "recurringCommitment",    // Its opening line, once the answer is yes
    "recurringEnds",          // Ends dropdown
    "recurringEndDateContainer", // End date container
    "recurringEndDate",       // End date input
    "createScheduleInput",    // Hidden input for schedule flag
    "scheduleFrequencyInput", // Hidden input for frequency
    "scheduleDaysInput",      // Hidden input for days
    "scheduleEndsInput",      // Hidden input for end date
    // Extra pets targets (logged-in users adding new pets)
    "extraPetsDisplay",        // Extra pets count display
    "extraPetsDecrementButton" // Extra pets decrement button
  ]

  static values = {
    readonly: Boolean,
    locationServiceId: String,
    price: Number,
    selectedDates: { type: Array, default: [] },
    currentMonth: String, // YYYY-MM format
    pets: { type: Array, default: [] }, // Array of pet objects { id, prefix_id, name, species, pp_pet_id }
    quantity: { type: Number, default: 1 }, // Manual quantity (for non-logged-in users)
    userLoggedIn: { type: Boolean, default: false }, // Whether user is logged in
    phantomKey: String, // Key for tracking phantom bookings (generated on first phantom creation)
    extraPets: { type: Number, default: 0 }, // Extra pet slots for logged-in users adding new pets
    dayPets: { type: Object, default: {} }, // date -> pet prefix ids, for days narrowed to a subset
    // date -> pet prefix ids the customer is HOLDING, for the whole forward window rather
    // than the month on screen. Read from the shard at page load; a hold two months out is
    // the one they are most likely to have forgotten about.
    heldDates: { type: Object, default: {} },
    // Where the Days step saves the selection as it changes. Absent elsewhere.
    autosaveUrl: String
  }

  connect() {
    // Picking a day grows the step under the calendar — the summary, the per-pet lines,
    // the repeat box — some of it a moment after the tap. With the button at the bottom of
    // the screen, Chrome's scroll anchoring kept the button still by scrolling the page, so
    // the days slid a row under the finger: mid-press it picked the day below, and between
    // taps the next tap landed a row off (staging UAT 1 Oct, DC-04). On this step nothing
    // below the calendar may move what is under the finger, so anchoring is off for as long
    // as the calendar is on the page. On <body> as well as <html>: Chrome kept anchoring
    // with it set on <html> alone.
    this.setScrollAnchoring('none')

    // Which pets each selected day applies to, keyed by date. A date absent from here
    // means "every selected pet" — the normal case. Only a day the customer resolved
    // through the partial prompt narrows to a subset. Spec step 5.
    // Seeded from the server so a customer who stepped back to add a pet returns to the
       // days they had already chosen, narrowed days included, rather than an empty month.
    this.dayPets = { ...this.dayPetsValue }
    // Whose days a tap changes: null for every pet ticked, or one pet's prefix id. The pet
    // pills only say who is coming; without this there was no way to give two dogs
    // different days, and swapping the ticked pet silently handed its days to the other
    // (staging UAT 15 Oct, B114).
    this.pickingFor = null
    // The pets ticked as of the last change, so a pill change can tell who joined and who
    // left — the days a pet had must go with it, not pass to whoever is ticked next.
    this.knownPetIds = this.getSelectedPetIds()
    this.holdsAcknowledged = false
    // Holds this session has given back — deselecting a day deletes its phantom, and
    // warning about a day the customer just deliberately dropped would be nonsense.
    this.clearedHolds = new Set()
    // Initialize to current month if not set
    if (!this.currentMonthValue) {
      const today = new Date()
      this.currentMonthValue = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`
    }

    // Generate a unique phantom key for this session (used to track phantom bookings)
    if (!this.phantomKeyValue) {
      this.phantomKeyValue = `dc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    }

    // Initialize quantity from the hidden input if in quantity selector mode
    if (this.hasQuantityDisplayTarget) {
      const quantityInput = this.element.querySelector('input[name="order_item[quantity]"]')
      if (quantityInput) {
        this.quantityValue = parseInt(quantityInput.value, 10) || 1
      }
      this.updateDecrementButton()
    }

    this.availabilityData = {}
    this.phantomErrors = {} // Track dates that failed phantom creation

    // Restore any dates saved before login redirect
    this.restorePostLoginDates()

    // What the server already has, so opening the step does not save it straight back.
    this.lastDraft = JSON.stringify(this.draftPayload())
    this.flushDraft = this.flushDraft.bind(this)
    window.addEventListener('pagehide', this.flushDraft)

    // Drag selection state
    this.isDragging = false
    this.dragStartDate = null
    this.dragMode = null // 'add' or 'remove'
    this.draggedDates = new Set()

    // Mouse drags are followed on the document. Touch drags are followed on the day the
    // finger went down on instead — see startDrag.
    this.handleMouseUp = this.handleMouseUp.bind(this)
    this.handleMouseMove = this.handleMouseMove.bind(this)
    this.handleTouchEnd = this.handleTouchEnd.bind(this)
    this.handleTouchMove = this.handleTouchMove.bind(this)

    document.addEventListener('mouseup', this.handleMouseUp)
    document.addEventListener('mousemove', this.handleMouseMove)

    this.fetchCalendarData()
  }

  disconnect() {
    document.removeEventListener('mouseup', this.handleMouseUp)
    document.removeEventListener('mousemove', this.handleMouseMove)
    this.unbindTouchTarget()
    window.removeEventListener('pagehide', this.flushDraft)
    this.flushDraft()
    // Turbo keeps <html> across visits, so leaving the step must not leave the next page
    // unable to anchor.
    this.setScrollAnchoring('')
  }

  setScrollAnchoring(value) {
    document.documentElement.style.overflowAnchor = value
    document.body.style.overflowAnchor = value
  }

  // Navigation
  previousMonth() {
    const [year, month] = this.currentMonthValue.split('-').map(Number)
    const date = new Date(year, month - 2, 1) // month - 2 because month is 1-indexed and we want previous
    this.currentMonthValue = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
    this.fetchCalendarData()
  }

  nextMonth() {
    const [year, month] = this.currentMonthValue.split('-').map(Number)
    const date = new Date(year, month, 1) // month because month is 1-indexed
    this.currentMonthValue = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
    this.fetchCalendarData()
  }

  // Fetch calendar data from backend
  async fetchCalendarData() {
    this.showLoading()

    try {
      let url = `/daycare/${this.locationServiceIdValue}/calendar?month=${this.currentMonthValue}`

      // Include selected pet IDs to check for already-booked dates
      const selectedPetIds = this.getSelectedPetIds()
      if (selectedPetIds.length > 0) {
        const petParams = selectedPetIds.map(id => `pet_ids[]=${encodeURIComponent(id)}`).join('&')
        url += `&${petParams}`
      }

      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) throw new Error('Failed to fetch calendar data')

      const data = await response.json()

      if (data.success) {
        this.availabilityData = data.dates
        if (data.price) this.priceValue = data.price
        this.renderCalendar()
      } else {
        console.error('Calendar error:', data.error)
        this.renderError(data.error)
      }
    } catch (error) {
      console.error('Failed to load calendar:', error)
      this.renderError('Failed to load calendar')
    } finally {
      this.hideLoading()
    }
  }

  // Render the calendar grid
  renderCalendar() {
    const [year, month] = this.currentMonthValue.split('-').map(Number)
    const firstDay = new Date(year, month - 1, 1)
    const lastDay = new Date(year, month, 0)
    const startingDay = firstDay.getDay() // 0 = Sunday

    // Update month label
    if (this.hasMonthLabelTarget) {
      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                          'July', 'August', 'September', 'October', 'November', 'December']
      this.monthLabelTarget.textContent = `${monthNames[month - 1]} ${year}`
    }

    // Build calendar grid
    let html = ''

    // Day headers
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    html += '<div class="grid grid-cols-7 gap-1 mb-2">'
    dayNames.forEach(day => {
      html += `<div class="text-center text-xs font-medium text-gray-500 py-1">${day}</div>`
    })
    html += '</div>'

    // Calendar days
    html += '<div class="grid grid-cols-7 gap-1">'

    // Empty cells for days before the first
    for (let i = 0; i < startingDay; i++) {
      html += '<div class="aspect-square"></div>'
    }

    // Which of the rarer states this month draws, so the legend explains exactly those.
    this.statesShown = new Set()

    // Days of the month
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    for (let day = 1; day <= lastDay.getDate(); day++) {
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      const dateObj = new Date(year, month - 1, day)
      const availability = this.availabilityData[dateStr] || {}
      const isSelected = this.selectedDatesValue.includes(dateStr)
      const isPast = dateObj < today

      html += this.renderDay(day, dateStr, availability, isSelected, isPast)
    }

    html += '</div>'

    if (this.hasCalendarTarget) {
      this.calendarTarget.innerHTML = html
    }

    this.legendItemTargets.forEach(item => {
      item.classList.toggle('hidden', !this.statesShown.has(item.dataset.state))
    })

    this.updateSummary()
  }

  renderDay(day, dateStr, availability, isSelected, isPast) {
    // Check if ALL selected pets are already booked on this date
    // Only block the date if every selected pet has a booking - allows booking remaining pets
    const allPetsBooked = this.areAllSelectedPetsBooked(availability.booked_pets || [])
    const partlyBooked = !allPetsBooked && this.isPartlyBooked(dateStr)
    // A day this customer is HOLDING but has not booked. It reads as free everywhere
    // else and PTP refuses it, so it gets a state of its own rather than being offered.
    const held = !allPetsBooked && !isPast && this.isHeld(dateStr)

    // Determine status: pet booking takes priority over general availability
    let status
    if (isPast) {
      status = 'past'
    } else if (allPetsBooked) {
      status = 'booked'
    } else {
      status = availability.status || 'unavailable'
    }

    // Read-only on the public service page: it reports the diary, it is not a chooser.
    // A held day is clickable precisely so it can be asked about — that is the only way
    // the customer learns the hold exists and can release it.
    // Choosing for one pet, a day that pet is already booked into is not theirs to pick,
    // whoever else is free on it.
    const pickerBooked = this.pickingFor && this.bookedPetIdsOn(dateStr).includes(this.pickingFor)
    const isClickable = !this.readonlyValue && !isPast && !allPetsBooked && !pickerBooked &&
                        (status === 'available' || held)

    // Solid only when the day is fully what the current choice means: the pet being chosen
    // for is on it, or, choosing for everyone, every free pet is. A day that only some of
    // the pets are on is shown as theirs, not as everyone's.
    const onDay = isSelected ? this.petsForDate(dateStr) : []
    const fullyPicked = isSelected && this.isPickedForCurrent(dateStr)
    const partlyPicked = isSelected && !fullyPicked && onDay.length > 0

    let classes = 'aspect-square flex flex-col items-center justify-center text-sm rounded-full transition-all relative select-none '
    let icon = ''

    if (fullyPicked) {
      classes += 'bg-[var(--color-primary)] text-white font-semibold '
    } else if (partlyPicked) {
      this.statesShown?.add('partlyPicked')
      classes += 'bg-[var(--color-primary)]/15 border-2 border-[var(--color-primary)] text-[var(--color-primary)] font-semibold cursor-pointer '
    } else if (pickerBooked && status === 'available') {
      classes += 'bg-[var(--color-primary)]/20 text-[var(--color-primary)]/60 cursor-not-allowed '
    } else if (held) {
      this.statesShown?.add('held')
      classes += 'bg-[var(--color-warning-surface)] border-2 border-dashed border-[var(--color-warning-border)] text-[var(--color-warning-ink)] font-semibold cursor-pointer '
      icon = '<i class="fa-solid fa-clock absolute top-0.5 right-0.5 text-[10px] text-[var(--color-warning)]"></i>'
    } else if (partlyBooked && status === 'available') {
      this.statesShown?.add('partlyBooked')
      classes += 'bg-white border-2 border-[var(--color-primary)] text-[var(--color-primary)] font-semibold cursor-pointer '
    } else if (status === 'booked') {
      classes += 'bg-[var(--color-primary)]/20 text-[var(--color-primary)]/60 cursor-not-allowed '
    } else if (status === 'available') {
      classes += 'bg-white border border-gray-200 hover:border-[var(--color-primary)] cursor-pointer '
    } else if (status === 'full') {
      // Full is a fact, not a decision for the customer, so it stays neutral; the
      // warning colour is kept for a day they are holding.
      classes += 'bg-gray-100 text-gray-400 line-through cursor-not-allowed '
      icon = '<i class="fa-solid fa-ban absolute top-0.5 right-0.5 text-xs text-gray-400"></i>'
    } else if (status === 'closed' || status === 'blocked') {
      classes += 'bg-gray-100 text-gray-300 cursor-not-allowed '
    } else if (status === 'past') {
      classes += 'bg-gray-50 text-gray-300 cursor-not-allowed '
    } else {
      classes += 'bg-gray-100 text-gray-300 cursor-not-allowed '
    }

    // touch-none on a day that can be picked: left to the browser, a finger moving off a
    // day starts a scroll before the drag ever sees it (B112).
    if (isClickable) classes += 'touch-none '
    const dataAttrs = isClickable
      ? `data-action="mousedown->daycare-calendar#startDrag touchstart->daycare-calendar#startDrag" data-date="${dateStr}"`
      : ''

    const tooltip = this.getTooltip(status, availability)
    const titleAttr = tooltip ? `title="${tooltip}"` : ''

    // Which pets a day is for, whenever that is not simply "all of them". Letters rather
    // than a colour per pet: the flow keeps to the brand colour and neutrals.
    const label = this.dayLabel(onDay)
    const labelHtml = label ? `<span class="text-[9px] leading-none font-medium">${label}</span>` : ''

    return `
      <div class="${classes}" ${dataAttrs} ${titleAttr}>
        <span>${day}</span>
        ${labelHtml}
        ${icon}
      </div>
    `
  }

  // ---- Choosing days per pet ---------------------------------------------------
  // The days live per pet: a selected day carries the pets it is for (this.dayPets, absent
  // meaning every pet ticked). A tap changes the day for whoever is being chosen for.

  sameSet(a, b) {
    return a.length === b.length && a.every(id => b.includes(id))
  }

  // Is this day already everything a tap would make it? Then a tap takes it away.
  isPickedForCurrent(dateStr) {
    if (!this.selectedDatesValue.includes(dateStr)) return false
    const onDay = this.petsForDate(dateStr)
    if (this.pickingFor) return onDay.includes(this.pickingFor)

    const free = this.freePetIdsOn(dateStr)
    return free.length === 0 || free.every(id => onDay.includes(id))
  }

  // Letters for the pets on a day, shown only once there are two pets or more and the day
  // is not for all of them. One letter each unless two pets share one.
  dayLabel(onDay) {
    const selected = this.getSelectedPetIds()
    if (selected.length < 2 || onDay.length === 0 || this.sameSet(onDay, selected)) return ''

    const names = selected.map(id => (this.petByPrefixId(id) || {}).name || '?')
    const firsts = names.map(name => name.charAt(0).toUpperCase())
    const width = new Set(firsts).size === firsts.length ? 1 : 2
    return selected
      .filter(id => onDay.includes(id))
      .map(id => {
        const name = (this.petByPrefixId(id) || {}).name || '?'
        return name.charAt(0).toUpperCase() + name.slice(1, width).toLowerCase()
      })
      .join(' ')
  }

  // Store which pets a day is for. Nobody left means the day goes; everyone means no entry.
  setDayPets(dateStr, prefixIds) {
    const dates = this.selectedDatesValue.filter(d => d !== dateStr)

    if (prefixIds.length === 0) {
      delete this.dayPets[dateStr]
      this.selectedDatesValue = dates
      return
    }

    if (this.sameSet(prefixIds, this.getSelectedPetIds())) {
      delete this.dayPets[dateStr]
    } else {
      this.dayPets[dateStr] = prefixIds
    }
    this.selectedDatesValue = [...dates, dateStr].sort()
  }

  // Add or take away one day for whoever is being chosen for.
  pickDay(dateStr, mode) {
    const selected = this.selectedDatesValue.includes(dateStr)
    const onDay = selected ? this.petsForDate(dateStr) : []
    const pet = this.pickingFor

    if (pet) {
      if (mode === 'add') {
        if (onDay.includes(pet) || !this.freePetIdsOn(dateStr).includes(pet)) return
        this.setDayPets(dateStr, [...onDay, pet])
        if (this.userLoggedInValue) this.holdFor([pet], dateStr)
      } else {
        if (!onDay.includes(pet)) return
        const rest = onDay.filter(id => id !== pet)
        this.setDayPets(dateStr, rest)
        if (this.userLoggedInValue) rest.length === 0 ? this.deletePhantomForDate(dateStr) : this.rehold(dateStr)
      }
      return
    }

    if (mode === 'add') {
      // Before any pet is ticked a day is for nobody in particular; it goes to whoever is
      // ticked next.
      if (this.getSelectedPetIds().length === 0) {
        if (!selected) this.selectedDatesValue = [...this.selectedDatesValue, dateStr].sort()
        return
      }

      // A pet already booked into this day cannot go twice, so the day carries whoever
      // is actually free.
      const joining = this.freePetIdsOn(dateStr).filter(id => !onDay.includes(id))
      if (joining.length === 0) return
      this.setDayPets(dateStr, [...onDay, ...joining])
      if (this.userLoggedInValue) this.holdFor(joining, dateStr)
    } else if (selected) {
      delete this.dayPets[dateStr]
      this.selectedDatesValue = this.selectedDatesValue.filter(d => d !== dateStr)
      if (this.userLoggedInValue) this.deletePhantomForDate(dateStr)
    }
  }

  async holdFor(prefixIds, dateStr) {
    const synced = this.getSyncedPets().map(pet => pet.prefix_id)
    for (const prefixId of prefixIds.filter(id => synced.includes(id))) {
      await this.createPhantom(prefixId, [dateStr])
    }
  }

  // Holds are released per day, not per pet, so taking one pet off a day releases the day
  // and holds it again for the pets still on it.
  async rehold(dateStr) {
    await this.deletePhantom([dateStr])
    await this.createPhantomForDate(dateStr)
  }

  pickFor(event) {
    this.pickingFor = event.currentTarget.dataset.pickingFor || null
    this.renderCalendar()
  }

  renderPickingFor() {
    if (!this.hasPickingForTarget) return

    const selected = this.getSelectedPetIds()
    if (selected.length < 2) {
      this.pickingFor = null
      this.pickingForTarget.innerHTML = ''
      this.pickingForTarget.classList.add('hidden')
      return
    }
    if (this.pickingFor && !selected.includes(this.pickingFor)) this.pickingFor = null

    const button = (petId, label) => {
      const active = (this.pickingFor || '') === petId
      const look = active
        ? 'border-[var(--color-primary)] bg-[var(--color-primary)] text-white'
        : 'border-gray-200 text-gray-700 hover:border-[var(--color-primary)]'
      return `<button type="button" data-picking-for="${petId}" data-action="click->daycare-calendar#pickFor"
                class="px-3 py-1.5 rounded-full border-2 text-sm font-medium transition-colors ${look}"
                aria-pressed="${active}">${label}</button>`
    }

    const name = this.pickingFor ? (this.petByPrefixId(this.pickingFor) || {}).name : null
    const hint = name
      ? `Tap a day to add or remove it for ${name} only.`
      : 'Tap a day to book every pet on it. To give a pet different days, choose them here first.'

    this.pickingForTarget.innerHTML = `
      <p class="text-sm font-medium text-gray-700 mb-2">Choosing days for</p>
      <div class="flex flex-wrap gap-2">
        ${button('', 'All')}
        ${selected.map(id => button(id, (this.petByPrefixId(id) || {}).name || 'Pet')).join('')}
      </div>
      <p class="mt-2 text-xs text-gray-500">${hint}</p>
    `
    this.pickingForTarget.classList.remove('hidden')
  }

  // Ticked pets with no days at all. Continuing would put them on the booking for nothing.
  petsWithoutDays() {
    if (this.selectedDatesValue.length === 0) return []
    return this.getSelectedPetIds().filter(id =>
      !this.selectedDatesValue.some(date => this.petsForDate(date).includes(id))
    )
  }

  // Days narrowed by the customer's choice, not merely by a pet already being booked in.
  // A repeat schedule books every pet on the same weekdays, so it cannot follow these.
  hasChosenSplit() {
    return this.selectedDatesValue.some(date =>
      this.dayPets[date] && !this.sameSet(this.dayPets[date], this.freePetIdsOn(date))
    )
  }


  // ---- Per-pet day selection -------------------------------------------------
  // PTP rejects a whole order if any one pet is already booked on a chosen day, so the
  // flow must never offer a pet a day it already holds. A partly-booked day is therefore
  // not selectable in one tap: it asks what the customer meant. Spec step 5.

  // The fork asks one question and costs one tap. Signing in is an emailed code, so
  // putting it in front of a first-time visitor who only wants to know whether Tuesdays
  // are available would cost bookings to solve a problem they cannot have.
  continueAsNewCustomer() {
    if (this.hasSignInForkTarget) this.signInForkTarget.classList.add('hidden')
    if (this.hasCalendarBodyTarget) this.calendarBodyTarget.classList.remove('hidden')
  }

  petByPrefixId(prefixId) {
    return this.petsValue.find(pet => pet.prefix_id === prefixId)
  }

  bookedPtpIdsOn(dateStr) {
    const availability = this.availabilityData[dateStr] || {}
    return (availability.booked_pets || []).map(id => parseInt(id, 10))
  }

  // Selected pets NOT already booked into this day — the ones this booking can still
  // put in it. Holds are a separate state and are not what this answers.
  freePetIdsOn(dateStr) {
    const booked = this.bookedPtpIdsOn(dateStr)
    return this.getSelectedPetIds().filter(prefixId => {
      const pet = this.petByPrefixId(prefixId)
      if (!pet || !pet.pp_pet_id) return true
      return !booked.includes(parseInt(pet.pp_pet_id, 10))
    })
  }

  bookedPetIdsOn(dateStr) {
    const free = this.freePetIdsOn(dateStr)
    return this.getSelectedPetIds().filter(prefixId => !free.includes(prefixId))
  }

  // Some but not all of the selected pets are free. Neither "book everyone" nor "blocked"
  // is the right answer, so this is the case that has to ask.
  isPartlyBooked(dateStr) {
    const selected = this.getSelectedPetIds()
    if (selected.length < 2) return false
    const free = this.freePetIdsOn(dateStr)
    return free.length > 0 && free.length < selected.length
  }

  // The summary carries the day count and the total; on its own that says nothing about
  // whether it covers one dog or two, and the price is per pet per day. Naming them is
  // what lets the customer check the figure.
  updatePetSummary() {
    if (!this.hasSummaryPetsTarget) return

    // Sits inline after the day count — "2 days for Biscuit & Luna" — rather than on a
    // line of its own. It is one fact about one selection, not a second summary.
    const names = this.selectedPetNames()
    this.summaryPetsTarget.textContent = names.length === 0 ? '' : ` for ${this.toSentence(names)}`
  }

  selectedPetNames() {
    if (!this.hasPetCheckboxTarget) return []

    return this.petCheckboxTargets
      .filter(box => box.checked)
      .map(box => (this.petByPrefixId(box.value) || {}).name)
      .filter(Boolean)
  }

  toSentence(names) {
    if (names.length === 1) return names[0]
    return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`
  }

  // Selected pets HOLDING this date — a space reserved when they started booking it and
  // never finished. Not a booking: it is invisible to the booked-days check, but
  // uk_class_bookings_1 includes cb_is_phantom, so PTP will refuse the date until it is
  // cleared. Only ever this customer's own pets, because that key is per pet.
  heldPetIdsOn(dateStr) {
    const availability = this.availabilityData[dateStr] || {}
    const held = (availability.held_pets || []).map(id => parseInt(id, 10))
    if (held.length === 0) return []

    // A pet can hold a day it is ALSO booked into — the unique key counts a hold and a
    // booking separately, so both rows sit there. Nothing is owed on that day and the
    // customer has no decision to make, so its leftover hold is not worth raising.
    const free = this.freePetIdsOn(dateStr)
    // A pet already on this day in this selection holds it for this booking, not from an
    // earlier one.
    const own = this.selectedDatesValue.includes(dateStr) ? this.petsForDate(dateStr) : []

    return free.filter(id => !own.includes(id)).filter(prefixId => {
      const pet = this.petByPrefixId(prefixId)
      return pet && pet.pp_pet_id && held.includes(parseInt(pet.pp_pet_id, 10))
    })
  }

  isHeld(dateStr) {
    return this.heldPetIdsOn(dateStr).length > 0
  }

  petsForDate(dateStr) {
    return this.dayPets[dateStr] || this.getSelectedPetIds()
  }

  petNames(prefixIds) {
    return prefixIds.map(id => (this.petByPrefixId(id) || {}).name).filter(Boolean)
  }

  // Days this customer is HOLDING that are not in the booking.
  //
  // A hold is a space reserved by starting a booking and walking away. It blocks nobody
  // but them — their own pet cannot be offered the day again until it clears — so it is
  // worth one mention. Not mid-selection, though: interrupting every tap to explain a
  // hold is how the question ended up below the fold on a phone in the first place. It
  // is asked once, on the way out, where the customer already is.
  outstandingHeldDates() {
    const selected = this.getSelectedPetIds()

    return Object.entries(this.heldDatesValue)
      .filter(([dateStr]) => !this.selectedDatesValue.includes(dateStr))
      .filter(([dateStr]) => !this.clearedHolds.has(dateStr))
      .filter(([, prefixIds]) => prefixIds.some(id => selected.includes(id)))
      .map(([dateStr]) => dateStr)
      .sort()
  }

  checkOutstandingHolds(event) {
    if (!this.holdsAcknowledged && this.userLoggedInValue && this.hasNoticeTarget) {
      const outstanding = this.outstandingHeldDates()
      if (outstanding.length > 0) {
        event.preventDefault()
        this.showHoldNotice(outstanding)
        return
      }
    }

    // Continue stores the selection itself. A save still waiting would land after it.
    this.cancelDraftSave()
  }

  showHoldNotice(dates) {
    const labels = dates.map(dateStr =>
      new Date(`${dateStr}T00:00:00`).toLocaleDateString(undefined, {
        weekday: 'short', day: 'numeric', month: 'short'
      })
    )
    const list = labels.length === 1
      ? labels[0]
      : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`

    this.noticeTarget.dataset.dates = dates.join(',')
    this.noticeTarget.innerHTML = `
      <div class="rounded-xl border-2 border-[var(--color-warning-border)] bg-[var(--color-warning-surface)] p-4 space-y-3">
        <div>
          <p class="font-semibold text-sm text-[var(--color-warning-ink)]">
            You're still holding ${labels.length === 1 ? 'a day' : `${labels.length} days`}
          </p>
          <p class="text-sm text-[var(--color-warning-ink)]">
            ${list} &mdash; you started booking ${labels.length === 1 ? 'this day' : 'these days'}
            before and didn't finish, so the ${labels.length === 1 ? 'space is' : 'spaces are'}
            still being held for you. ${labels.length === 1 ? "It isn't" : "They aren't"} part of this booking.
          </p>
        </div>
        <div class="flex gap-2">
          <button type="button" class="flex-1 btn btn-quiet text-sm"
                  data-action="click->daycare-calendar#addHeldDays">Let me add ${labels.length === 1 ? 'it' : 'them'}</button>
          <button type="button" class="flex-1 btn btn-primary text-sm"
                  data-action="click->daycare-calendar#continueWithoutHolds">Continue without ${labels.length === 1 ? 'it' : 'them'}</button>
        </div>
      </div>
    `
    this.noticeTarget.classList.remove('hidden')
    this.noticeTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }

  // "Let me add it" has to mean exactly that. It used to only close the panel, so the
  // customer had to go and find a day that could be two months away, and the next Continue
  // asked the same question again (B98). Each day goes to the ticked pets holding it — the
  // hold is theirs, not everyone's — and holding it again under this page's key adopts the
  // old hold rather than tripping over it.
  addHeldDays() {
    if (!this.hasNoticeTarget) return
    const dates = (this.noticeTarget.dataset.dates || '').split(',').filter(Boolean)
    const ticked = this.getSelectedPetIds()
    const added = []

    dates.forEach(dateStr => {
      let holders = (this.heldDatesValue[dateStr] || []).filter(id => ticked.includes(id))
      // A pet already booked into the day cannot go twice. Only a fetched month can say.
      if (this.availabilityData[dateStr]) {
        const free = this.freePetIdsOn(dateStr)
        holders = holders.filter(id => free.includes(id))
      }
      if (holders.length === 0) return

      const onDay = this.selectedDatesValue.includes(dateStr) ? this.petsForDate(dateStr) : []
      this.setDayPets(dateStr, [...new Set([...onDay, ...holders])])
      this.clearedHolds.delete(dateStr)
      if (this.userLoggedInValue) this.holdFor(holders, dateStr)
      added.push(dateStr)
    })

    this.dismissNotice()
    if (added.length === 0) return

    // Show them the day they have just added. Left on the month they were in, the count
    // goes up with nothing on screen to say why.
    const month = added.sort()[0].slice(0, 7)
    if (month !== this.currentMonthValue) {
      this.currentMonthValue = month
      this.fetchCalendarData()
    } else {
      this.renderCalendar()
    }
  }

  dismissNotice() {
    if (!this.hasNoticeTarget) return
    this.noticeTarget.innerHTML = ''
    this.noticeTarget.classList.add('hidden')
  }

  async continueWithoutHolds(event) {
    const button = event.currentTarget
    const dates = (this.noticeTarget.dataset.dates || '').split(',').filter(Boolean)

    button.disabled = true
    button.textContent = 'Releasing…'

    // Free them now rather than leaving them to time out. The customer has just said they
    // don't want these days, and until the hold clears they are the one person who cannot
    // book them.
    await this.releaseHolds(dates)

    this.holdsAcknowledged = true
    this.dismissNotice()
    if (this.hasFormTarget) this.formTarget.requestSubmit()
  }

  async releaseHolds(dates) {
    if (dates.length === 0) return

    try {
      await fetch(`/daycare/${this.locationServiceIdValue}/holds`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken(),
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ pet_ids: this.getSelectedPetIds(), dates: dates })
      })
    } catch (error) {
      // Failing to free them costs the customer nothing — ExpireHoldsJob takes them
      // within the quarter hour — so it must never stand between them and the booking.
      console.error('Failed to release holds:', error)
    }
  }

  // Check if ALL selected pets are in the booked_pets list
  // Returns true only if every selected pet already has a booking on this date
  areAllSelectedPetsBooked(bookedPetIds) {
    if (!bookedPetIds || bookedPetIds.length === 0) return false

    // Get pp_pet_ids of selected pets (as integers)
    const selectedPtpPetIds = this.getSelectedPtpPetIds()
    if (selectedPtpPetIds.length === 0) return false

    // Convert booked_pets to integers for comparison (handles string vs int mismatch)
    const bookedPetIdsInt = bookedPetIds.map(id => parseInt(id, 10))

    // Only block if ALL selected pets are booked - allows booking remaining pets
    return selectedPtpPetIds.every(ptpId => bookedPetIdsInt.includes(ptpId))
  }

  // Get PTP pet IDs for selected pets
  getSelectedPtpPetIds() {
    if (!this.hasPetCheckboxTarget) return []

    const selectedPetIds = this.petCheckboxTargets
      .filter(cb => cb.checked)
      .map(cb => cb.value)

    return this.petsValue
      .filter(pet => selectedPetIds.includes(pet.prefix_id) && pet.pp_pet_id)
      .map(pet => parseInt(pet.pp_pet_id, 10))  // Ensure integer
  }

  getTooltip(status, availability) {
    switch (status) {
      case 'booked': return 'Already booked'
      case 'full': return 'Fully booked'
      case 'closed': return 'Closed'
      case 'blocked': return availability.reason || 'Not available'
      case 'past': return 'Past date'
      case 'available': return null
      default: return 'Not available'
    }
  }

  // Start drag selection
  startDrag(event) {
    const dateStr = event.currentTarget.dataset.date
    if (!dateStr) return

    // Prevent text selection during drag
    event.preventDefault()

    this.isDragging = true
    this.dragStartDate = dateStr
    this.draggedDates = new Set()
    this.lastPointer = this.pointOf(event)

    // Removing if the day is already all it can be for whoever is being chosen for
    this.dragMode = this.isPickedForCurrent(dateStr) ? 'remove' : 'add'

    // A touch's moves and its end are delivered to the element it started on, whatever is
    // under the finger by then — and the render below replaces that element. Detached, its
    // events never reached a listener on the document, so on a phone a drag stopped at the
    // first day (B112). Listening on the element itself, they arrive wherever it is.
    if (event.type === 'touchstart') this.bindTouchTarget(event.currentTarget)

    // Apply to the first date
    this.applyDragToDate(dateStr)
    this.renderCalendar()
  }

  // Handle mouse move during drag
  handleMouseMove(event) {
    if (!this.isDragging || !this.pointerMoved(event)) return

    // closest, because the point under the cursor is the SPAN holding the day number, not
    // the cell carrying data-date. Reading dataset.date straight off it was always
    // undefined, so a drag never got past the day it started on.
    const dateStr = this.dateUnder(event.clientX, event.clientY)
    if (dateStr && !this.draggedDates.has(dateStr)) {
      this.applyDragToDate(dateStr)
      this.renderCalendar()
    }
  }

  dateUnder(x, y) {
    return document.elementFromPoint(x, y)?.closest('[data-date]')?.dataset?.date
  }

  pointOf(event) {
    const source = event.touches ? event.touches[0] : event
    return source ? { x: source.clientX, y: source.clientY } : null
  }

  // Chrome fires mousemove whenever the layout shifts under a still cursor. That is the
  // page moving, not the pointer, and must never extend a drag — a press that never moved
  // picked the day below whenever the step grew under it.
  pointerMoved(event) {
    const point = this.pointOf(event)
    if (!point) return false
    const moved = !this.lastPointer || point.x !== this.lastPointer.x || point.y !== this.lastPointer.y
    this.lastPointer = point
    return moved
  }

  bindTouchTarget(element) {
    this.unbindTouchTarget()
    this.touchTarget = element
    element.addEventListener('touchmove', this.handleTouchMove, { passive: false })
    element.addEventListener('touchend', this.handleTouchEnd)
    element.addEventListener('touchcancel', this.handleTouchEnd)
  }

  unbindTouchTarget() {
    if (!this.touchTarget) return
    this.touchTarget.removeEventListener('touchmove', this.handleTouchMove)
    this.touchTarget.removeEventListener('touchend', this.handleTouchEnd)
    this.touchTarget.removeEventListener('touchcancel', this.handleTouchEnd)
    this.touchTarget = null
  }

  // Handle touch move during drag
  handleTouchMove(event) {
    if (!this.isDragging) return
    // Every move, not only the ones that reach a new day: once the browser has been allowed
    // one move it has started scrolling, and the drag is lost under it.
    event.preventDefault()
    if (!this.pointerMoved(event)) return

    const touch = event.touches[0]
    const dateStr = this.dateUnder(touch.clientX, touch.clientY)
    if (dateStr && !this.draggedDates.has(dateStr)) {
      this.applyDragToDate(dateStr)
      this.renderCalendar()
    }
  }

  // Handle mouse up to end drag
  handleMouseUp() {
    if (this.isDragging) {
      this.isDragging = false
      this.dragStartDate = null
      this.dragMode = null
      this.draggedDates = new Set()
      this.lastPointer = null
    }
  }

  // Handle touch end to end drag
  handleTouchEnd() {
    this.unbindTouchTarget()
    this.handleMouseUp()
  }

  // Apply drag action to a date
  applyDragToDate(dateStr) {
    // Check if date is available
    const availability = this.availabilityData[dateStr]
    if (!availability || availability.status !== 'available') return

    // Check if date is in the past
    const [year, month, day] = dateStr.split('-').map(Number)
    const dateObj = new Date(year, month - 1, day)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    if (dateObj < today) return

    this.draggedDates.add(dateStr)

    // Nothing is refused and nothing is asked mid-selection — the review lists the pets
    // per day, and any day still left held is raised on Continue.
    this.pickDay(dateStr, this.dragMode)
  }

  // Toggle single date (for click without drag)
  toggleDate(dateStr) {
    this.pickDay(dateStr, this.isPickedForCurrent(dateStr) ? 'remove' : 'add')
    this.renderCalendar()
  }

  // Handle pet selection change
  async updatePetSelection(event) {
    // Each day keeps the pets it was chosen for. A pet unticked takes its days with it —
    // they were its days, not whoever's is ticked next — and a pet ticked joins every day
    // already chosen, as everyone does when choosing for All. Clearing the lot here, as
    // this used to, is what let a day picked for one dog quietly become another's (B114).
    const before = this.knownPetIds
    const now = this.getSelectedPetIds()
    const joined = now.filter(id => !before.includes(id))
    const left = before.filter(id => !now.includes(id))
    this.knownPetIds = now
    if (this.pickingFor && left.includes(this.pickingFor)) this.pickingFor = null

    const touched = []
    this.selectedDatesValue.forEach(dateStr => {
      const was = this.dayPets[dateStr] || before
      if (left.some(id => was.includes(id))) touched.push(dateStr)
      this.dayPets[dateStr] = [...was.filter(id => !left.includes(id)), ...joined]
    })
    // A day nobody is left on goes — unless no pet was ticked when it was picked, in which
    // case it was for nobody in particular and now belongs to whoever has just been ticked.
    this.selectedDatesValue = this.selectedDatesValue.filter(dateStr => {
      if (this.dayPets[dateStr].length > 0) return true
      delete this.dayPets[dateStr]
      if (this.userLoggedInValue) this.deletePhantomForDate(dateStr)
      return false
    })

    this.dismissNotice()
    const checkbox = event.target
    const petId = checkbox.value
    const isChecked = checkbox.checked

    // Find the corresponding pet card and update its visual state
    const petCard = this.petCardTargets.find(card => card.dataset.petId === petId)
    if (petCard) {
      // One class; the appearance lives in room-picker.css, tick included. Four utility
      // strings kept in step across a template and this file is one more thing to get
      // wrong than a single state class.
      const checkIcon = petCard.querySelector('.pet-check-icon')
      checkIcon?.classList.remove('hidden')
      petCard.classList.toggle('is-selected', isChecked)
    }

    // Refetch first: booked_pets is answered for the pets asked about, so the days cannot
    // be judged against a pet the shard was never asked about.
    await this.fetchCalendarData()
    this.reconcileSelectedDates()

    if (this.userLoggedInValue) {
      touched.filter(d => this.selectedDatesValue.includes(d)).forEach(d => this.rehold(d))
      joined.forEach(prefixId => {
        const dates = this.selectedDatesValue.filter(d => this.petsForDate(d).includes(prefixId))
        if (dates.length > 0 && this.getSyncedPets().some(pet => pet.prefix_id === prefixId)) {
          this.createPhantom(prefixId, dates)
        }
      })
    }
    this.renderCalendar()
  }

  // Days can be chosen before the pets are, and a day chosen then was chosen for nobody in
  // particular. Ticking a pet already booked into one of those days has to be answered
  // here rather than left to fail at the end: the day belongs to whoever is still free on
  // it, and a day with nobody free is not a day this booking can have.
  reconcileSelectedDates() {
    const selected = this.getSelectedPetIds()
    if (selected.length === 0) return

    const kept = []
    const dropped = []

    this.selectedDatesValue.forEach(dateStr => {
      // Only months the calendar has actually fetched can be judged. Treating an unknown
      // day as unbookable would silently delete days the customer chose.
      if (!this.availabilityData[dateStr]) return kept.push(dateStr)

      const free = this.freePetIdsOn(dateStr)
      const going = this.petsForDate(dateStr).filter(id => free.includes(id))
      if (going.length === 0) return dropped.push(dateStr)

      kept.push(dateStr)
      if (this.sameSet(going, selected)) {
        delete this.dayPets[dateStr]
      } else {
        this.dayPets[dateStr] = going
      }
    })

    if (dropped.length === 0) return

    dropped.forEach(dateStr => {
      delete this.dayPets[dateStr]
      if (this.userLoggedInValue) this.deletePhantomForDate(dateStr)
    })

    this.selectedDatesValue = kept
    this.renderCalendar()
    this.showDroppedNote(dropped)
  }

  showDroppedNote(dates) {
    if (!this.hasNoticeTarget) return

    const labels = dates.map(dateStr =>
      new Date(`${dateStr}T00:00:00`).toLocaleDateString(undefined, {
        weekday: 'short', day: 'numeric', month: 'short'
      })
    )
    const list = labels.length === 1
      ? labels[0]
      : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`
    const one = labels.length === 1

    this.noticeTarget.innerHTML = `
      <div class="rounded-xl border-2 border-[var(--color-warning-border)] bg-[var(--color-warning-surface)] p-4 space-y-3">
        <div>
          <p class="font-semibold text-sm text-[var(--color-warning-ink)]">
            ${one ? 'A day was removed' : `${labels.length} days were removed`}
          </p>
          <p class="text-sm text-[var(--color-warning-ink)]">
            ${list} &mdash; every pet you've chosen is already booked in
            ${one ? 'that day' : 'those days'}, so there is nothing left to book.
          </p>
        </div>
        <button type="button" class="btn btn-quiet text-sm w-full sm:w-auto"
                data-action="click->daycare-calendar#dismissNotice">Got it</button>
      </div>
    `
    this.noticeTarget.classList.remove('hidden')
    this.noticeTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }

  // Quantity selector methods (for non-logged-in users)
  incrementQuantity() {
    if (this.quantityValue < 10) { // Max 10 pets
      this.quantityValue++
      this.updateQuantityDisplay()
      this.updateSummary()
    }
  }

  decrementQuantity() {
    if (this.quantityValue > 1) {
      this.quantityValue--
      this.updateQuantityDisplay()
      this.updateSummary()
    }
  }

  updateQuantityDisplay() {
    if (this.hasQuantityDisplayTarget) {
      this.quantityDisplayTarget.textContent = this.quantityValue
    }
    this.updateDecrementButton()
  }

  updateDecrementButton() {
    if (this.hasDecrementButtonTarget) {
      this.decrementButtonTarget.disabled = this.quantityValue <= 1
    }
  }

  // Extra pets methods (for logged-in users adding pets not yet registered)
  incrementExtraPets() {
    if (this.extraPetsValue < 10) {
      this.extraPetsValue++
      this.updateExtraPetsDisplay()
      this.updateSummary()
    }
  }

  decrementExtraPets() {
    if (this.extraPetsValue > 0) {
      this.extraPetsValue--
      this.updateExtraPetsDisplay()
      this.updateSummary()
    }
  }

  updateExtraPetsDisplay() {
    if (this.hasExtraPetsDisplayTarget) {
      this.extraPetsDisplayTarget.textContent = this.extraPetsValue
    }
    if (this.hasExtraPetsDecrementButtonTarget) {
      this.extraPetsDecrementButtonTarget.disabled = this.extraPetsValue <= 0
    }
  }

  // Get number of selected pets
  getSelectedPetCount() {
    // If using quantity selector (not logged in)
    if (this.hasQuantityDisplayTarget) {
      return this.quantityValue
    }
    // If using pet checkboxes (logged in with pets)
    if (this.hasPetCheckboxTarget) {
      return this.petCheckboxTargets.filter(cb => cb.checked).length + this.extraPetsValue
    }
    // Fallback to hidden quantity field
    const quantityInput = this.element.querySelector('input[name="order_item[quantity]"]')
    return quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1
  }

  // Get selected pet IDs
  getSelectedPetIds() {
    if (!this.hasPetCheckboxTarget) return []
    return this.petCheckboxTargets.filter(cb => cb.checked).map(cb => cb.value)
  }

  // Get selected dates (for external use)
  getSelectedDates() {
    return this.selectedDatesValue
  }

  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove('hidden')
    }
    if (this.hasCalendarTarget) {
      this.calendarTarget.classList.add('opacity-50')
    }
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add('hidden')
    }
    if (this.hasCalendarTarget) {
      this.calendarTarget.classList.remove('opacity-50')
    }
  }

  renderError(message) {
    if (this.hasCalendarTarget) {
      this.calendarTarget.innerHTML = `
        <div class="text-center py-8 text-gray-500">
          <i class="fa-solid fa-exclamation-circle text-2xl mb-2"></i>
          <p>${message}</p>
        </div>
      `
    }
  }

  // ============================================
  // Phantom Booking Methods
  // ============================================

  // Get CSRF token for API requests
  getCSRFToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  // Get selected pets that have pp_pet_id (synced to PTP)
  getSyncedPets() {
    if (!this.hasPetCheckboxTarget) return []

    const selectedPetIds = this.petCheckboxTargets
      .filter(cb => cb.checked)
      .map(cb => cb.value)

    return this.petsValue.filter(pet =>
      selectedPetIds.includes(pet.prefix_id) && pet.pp_pet_id
    )
  }

  // Create phantom bookings for a single date (for all selected pets)
  async createPhantomForDate(dateStr) {
    // A pet already booked into this day must not be asked for a hold: the shard answers
    // "already booked" and the whole call fails, so the pets who ARE free would lose
    // their space over a pet that never needed one.
    const going = this.petsForDate(dateStr)
    const pets = this.getSyncedPets().filter(pet => going.includes(pet.prefix_id))
    if (pets.length === 0) return

    for (const pet of pets) {
      await this.createPhantom(pet.prefix_id, [dateStr])
    }
  }

  // Delete phantom bookings for a single date
  async deletePhantomForDate(dateStr) {
    this.clearedHolds.add(dateStr)
    await this.deletePhantom([dateStr])
  }

  // Create phantom bookings via API
  async createPhantom(petPrefixId, dates) {
    try {
      const response = await fetch(`/daycare/${this.locationServiceIdValue}/phantoms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': this.getCSRFToken()
        },
        body: JSON.stringify({
          pet_id: petPrefixId,
          dates: dates,
          phantom_key: this.phantomKeyValue
        })
      })

      const data = await response.json()

      if (!data.success) {
        console.warn('Phantom creation failed:', data.error)
        // Mark these dates as having errors
        dates.forEach(d => this.phantomErrors[d] = data.error)
      }

      return data
    } catch (error) {
      console.error('Phantom API error:', error)
      dates.forEach(d => this.phantomErrors[d] = error.message)
      return { success: false, error: error.message }
    }
  }

  // Delete phantom bookings via API
  async deletePhantom(dates = null) {
    try {
      const body = { phantom_key: this.phantomKeyValue }
      if (dates) body.dates = dates

      const response = await fetch(`/daycare/${this.locationServiceIdValue}/phantoms`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': this.getCSRFToken()
        },
        body: JSON.stringify(body)
      })

      const data = await response.json()

      if (data.success && dates) {
        // Clear errors for deleted dates
        dates.forEach(d => delete this.phantomErrors[d])
      }

      return data
    } catch (error) {
      console.error('Phantom delete error:', error)
      return { success: false, error: error.message }
    }
  }

  // Create phantoms for all currently selected dates (called after login)
  // Returns object with results: { success: boolean, created: number, failed: string[] }
  async createPhantomsForAllSelected() {
    const dates = this.selectedDatesValue
    if (dates.length === 0) return { success: true, created: 0, failed: [] }

    const pets = this.getSyncedPets()
    if (pets.length === 0) {
      console.warn('No synced pets available for phantom creation')
      return { success: false, created: 0, failed: dates, error: 'No pets synced to PTP' }
    }

    let created = 0
    const failed = []

    for (const pet of pets) {
      const result = await this.createPhantom(pet.prefix_id, dates)
      if (result.success) {
        created += dates.length
      } else {
        // Some dates may have failed
        failed.push(...dates)
      }
    }

    // Check availability again and remove any dates that are no longer available
    await this.fetchCalendarData()
    const unavailableDates = dates.filter(d => {
      const availability = this.availabilityData[d]
      return !availability || availability.status !== 'available'
    })

    if (unavailableDates.length > 0) {
      // Remove unavailable dates from selection
      this.selectedDatesValue = this.selectedDatesValue.filter(d => !unavailableDates.includes(d))
      failed.push(...unavailableDates)
      this.renderCalendar()
    }

    return {
      success: failed.length === 0,
      created,
      failed: [...new Set(failed)] // dedupe
    }
  }

  // Called when user logs in - creates phantoms for pre-selected dates
  // This should be called from the login success handler
  async onUserLoggedIn(pets = []) {
    this.userLoggedInValue = true

    // Update pets if provided
    if (pets.length > 0) {
      this.petsValue = pets
    }

    // Create phantoms for all selected dates
    if (this.selectedDatesValue.length > 0) {
      const result = await this.createPhantomsForAllSelected()

      if (result.failed.length > 0) {
        // Show error to user
        const message = `Some dates are no longer available: ${result.failed.join(', ')}`
        console.warn(message)
        // Could dispatch a custom event for the UI to handle
        this.dispatch('phantomConflict', { detail: { dates: result.failed, message } })
      }

      return result
    }

    return { success: true, created: 0, failed: [] }
  }

  // Clear all selections and delete any phantoms
  async clearSelection() {
    // Delete phantoms first if logged in
    if (this.userLoggedInValue && this.selectedDatesValue.length > 0) {
      await this.deletePhantom()
    }

    this.selectedDatesValue = []
    this.dayPets = {}
    this.phantomErrors = {}
    this.clearStoredDates()
    this.renderCalendar()
  }

  // ============================================
  // Post-Login Date Restoration
  // ============================================

  // LocalStorage key for storing dates across login redirect
  get storageKey() {
    return `daycare_dates_${this.locationServiceIdValue}`
  }

  // Save selected dates to localStorage before login redirect
  // Call this when user clicks login/continue without being logged in
  saveDatesForLogin() {
    if (this.selectedDatesValue.length === 0) return

    const data = {
      dates: this.selectedDatesValue,
      savedAt: Date.now(),
      phantomKey: this.phantomKeyValue
    }

    try {
      localStorage.setItem(this.storageKey, JSON.stringify(data))
    } catch (e) {
      console.warn('Failed to save dates to localStorage:', e)
    }
  }

  // Restore dates after login redirect and create phantoms
  async restorePostLoginDates() {
    // Only restore if user is now logged in
    if (!this.userLoggedInValue) return

    try {
      const stored = localStorage.getItem(this.storageKey)
      if (!stored) return

      const data = JSON.parse(stored)

      // Don't restore if data is older than 30 minutes
      if (Date.now() - data.savedAt > 30 * 60 * 1000) {
        this.clearStoredDates()
        return
      }

      // Restore the dates
      if (data.dates && data.dates.length > 0) {
        this.selectedDatesValue = data.dates

        // Use the same phantom key if available
        if (data.phantomKey) {
          this.phantomKeyValue = data.phantomKey
        }

        // Clear stored data now that we've restored
        this.clearStoredDates()

        // Wait for calendar data to load, then create phantoms
        // The fetchCalendarData in connect() will render the calendar
        // We need to wait for that before creating phantoms
        setTimeout(async () => {
          const result = await this.createPhantomsForAllSelected()

          if (result.failed.length > 0) {
            this.dispatch('phantomConflict', {
              detail: {
                dates: result.failed,
                message: `Some dates are no longer available: ${result.failed.join(', ')}`
              }
            })
          }

          this.updateSummary()
        }, 500) // Give time for calendar data to load
      }
    } catch (e) {
      console.warn('Failed to restore dates from localStorage:', e)
      this.clearStoredDates()
    }
  }

  // Clear stored dates from localStorage
  clearStoredDates() {
    try {
      localStorage.removeItem(this.storageKey)
    } catch (e) {
      // Ignore errors
    }
  }

  // Check if there are dates to restore (for UI to show loading state)
  hasStoredDates() {
    try {
      return localStorage.getItem(this.storageKey) !== null
    } catch (e) {
      return false
    }
  }

  // ============================================
  // Recurring Schedule Methods
  // ============================================

  // Toggle recurring options visibility
  toggleRecurring(event) {
    const isEnabled = event.target.checked

    // Toggle options visibility
    if (this.hasRecurringOptionsTarget) {
      this.recurringOptionsTarget.classList.toggle('hidden', !isEnabled)
    }

    // Unticked the card ASKS; ticked it CONFIRMS. Two different acts, so they are two
    // different sentences rather than one that has to serve both.
    if (this.hasRecurringInviteTarget) {
      this.recurringInviteTarget.classList.toggle('hidden', isEnabled)
    }
    if (this.hasRecurringLeadInTarget) {
      this.recurringLeadInTarget.classList.toggle('hidden', !isEnabled)
    }

    // Toggle card styling
    if (this.hasRecurringCardTarget) {
      if (isEnabled) {
        this.recurringCardTarget.classList.remove('border-gray-200')
        this.recurringCardTarget.classList.add('border-[var(--color-primary)]', 'bg-[var(--color-primary)]/5')
      } else {
        this.recurringCardTarget.classList.add('border-gray-200')
        this.recurringCardTarget.classList.remove('border-[var(--color-primary)]', 'bg-[var(--color-primary)]/5')
      }

      // Update icon color
      const icon = this.recurringCardTarget.querySelector('.fa-repeat')
      if (icon) {
        if (isEnabled) {
          icon.classList.remove('text-gray-400')
          icon.classList.add('text-[var(--color-primary)]')
        } else {
          icon.classList.add('text-gray-400')
          icon.classList.remove('text-[var(--color-primary)]')
        }
      }

      // Update icon background
      const iconBg = icon?.closest('div')
      if (iconBg) {
        if (isEnabled) {
          iconBg.classList.remove('bg-gray-100')
          iconBg.classList.add('bg-[var(--color-primary)]/10')
        } else {
          iconBg.classList.add('bg-gray-100')
          iconBg.classList.remove('bg-[var(--color-primary)]/10')
        }
      }
    }

    // Update checkmark indicator
    if (this.hasRecurringCheckIndicatorTarget) {
      if (isEnabled) {
        this.recurringCheckIndicatorTarget.classList.remove('border-gray-200')
        this.recurringCheckIndicatorTarget.classList.add('border-[var(--color-primary)]', 'bg-[var(--color-primary)]')
        this.recurringCheckIndicatorTarget.querySelector('i')?.classList.add('opacity-100')
        this.recurringCheckIndicatorTarget.querySelector('i')?.classList.remove('opacity-0')
      } else {
        this.recurringCheckIndicatorTarget.classList.add('border-gray-200')
        this.recurringCheckIndicatorTarget.classList.remove('border-[var(--color-primary)]', 'bg-[var(--color-primary)]')
        this.recurringCheckIndicatorTarget.querySelector('i')?.classList.remove('opacity-100')
        this.recurringCheckIndicatorTarget.querySelector('i')?.classList.add('opacity-0')
      }
    }

    if (this.hasCreateScheduleInputTarget) {
      this.createScheduleInputTarget.disabled = !isEnabled
    }

    // Update button text
    this.updateButtonForRecurring(isEnabled)

    // The confirming line only belongs there once the answer is yes; the rest of the
    // explainer is what the question is ABOUT, so it stays either way.
    if (this.hasRecurringCommitmentTarget) {
      this.recurringCommitmentTarget.classList.toggle('hidden', !isEnabled)
    }

    this.updateRecurringDaysFromSelection()
  }

  // Update button text based on recurring mode
  updateButtonForRecurring(isRecurring) {
    if (!this.hasAddButtonTarget) return

    if (isRecurring && this.selectedDatesValue.length > 0) {
      this.addButtonTarget.textContent = 'Create Schedule'
    } else if (this.selectedDatesValue.length > 0) {
      this.addButtonTarget.textContent = 'Add'
    } else {
      this.addButtonTarget.textContent = 'Select'
    }
  }

  // Update recurring day buttons based on selected dates
  updateRecurringDaysFromSelection() {
    if (!this.hasRecurringDayButtonTarget) return

    // Get days of week from selected dates
    const selectedDays = new Set()
    this.selectedDatesValue.forEach(dateStr => {
      const [year, month, day] = dateStr.split('-').map(Number)
      const date = new Date(year, month - 1, day)
      const dayName = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][date.getDay()]
      selectedDays.add(dayName)
    })

    // Get available days from calendar data
    const availableDays = this.getAvailableDaysOfWeek()

    // Update day buttons
    this.recurringDayButtonTargets.forEach(button => {
      const dayName = button.dataset.day
      const isAvailable = availableDays.has(dayName)
      const isSelected = selectedDays.has(dayName)

      button.disabled = !isAvailable

      if (isSelected && isAvailable) {
        button.classList.remove('bg-gray-100', 'bg-gray-50', 'text-gray-400', 'text-gray-300')
        button.classList.add('bg-[var(--color-primary)]', 'text-white')
      } else if (isAvailable) {
        button.classList.remove('bg-[var(--color-primary)]', 'bg-gray-50', 'text-white', 'text-gray-300')
        button.classList.add('bg-gray-100', 'text-gray-400')
      } else {
        button.classList.remove('bg-[var(--color-primary)]', 'bg-gray-100', 'text-white', 'text-gray-400')
        button.classList.add('bg-gray-50', 'text-gray-300')
      }
    })

    // Update hint text
    if (this.hasRecurringDaysHintTarget) {
      if (selectedDays.size > 0) {
        const dayNames = Array.from(selectedDays).map(d => d.charAt(0).toUpperCase() + d.slice(1, 3))
        this.recurringDaysHintTarget.textContent = `Repeating on ${dayNames.join(', ')}`
      } else {
        this.recurringDaysHintTarget.textContent = 'Select dates above to set recurring days'
      }
    }

    this.updateRecurringSummary(selectedDays)

    // Update hidden inputs
    this.updateRecurringHiddenInputs()
  }

  // Get available days of week from calendar data
  getAvailableDaysOfWeek() {
    const availableDays = new Set()

    Object.entries(this.availabilityData).forEach(([dateStr, data]) => {
      if (data.status === 'available') {
        const [year, month, day] = dateStr.split('-').map(Number)
        const date = new Date(year, month - 1, day)
        const dayName = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][date.getDay()]
        availableDays.add(dayName)
      }
    })

    return availableDays
  }

  // Toggle individual recurring day
  toggleRecurringDay(event) {
    const button = event.currentTarget
    if (button.disabled) return

    const isSelected = button.classList.contains('bg-[var(--color-primary)]')

    if (isSelected) {
      button.classList.remove('bg-[var(--color-primary)]', 'text-white')
      button.classList.add('bg-gray-100', 'text-gray-400')
    } else {
      button.classList.remove('bg-gray-100', 'text-gray-400')
      button.classList.add('bg-[var(--color-primary)]', 'text-white')
    }

    this.updateRecurringHiddenInputs()
    this.updateRecurringDaysHint()

    // Auto-select dates on calendar based on recurring days
    this.autoSelectRecurringDates()
  }

  // Auto-select the next 4 weeks of dates based on selected recurring days
  autoSelectRecurringDates() {
    const selectedDays = this.getSelectedRecurringDays()
    if (selectedDays.length === 0) {
      // Clear selection if no days selected
      this.selectedDatesValue = []
      this.renderCalendar()
      return
    }

    const dayMap = {
      sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
      thursday: 4, friday: 5, saturday: 6
    }

    const targetDayNums = selectedDays.map(d => dayMap[d]).sort((a, b) => a - b)
    const earliestDayNum = targetDayNums[0]
    const dates = []
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    // Get frequency (weekly = 1, fortnightly = 2)
    const frequency = this.hasRecurringFrequencyTarget ?
      (this.recurringFrequencyTarget.value === 'fortnightly' ? 2 : 1) : 1

    // Find the next occurrence of the EARLIEST selected day
    // This becomes the start of our 4-week period
    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)

    // Calculate days until the earliest selected day (from tomorrow)
    const tomorrowDayNum = tomorrow.getDay()
    let daysUntilStart = earliestDayNum - tomorrowDayNum
    if (daysUntilStart < 0) {
      daysUntilStart += 7 // Next week
    }

    // First full week starts on the earliest selected day
    const firstWeekStart = new Date(tomorrow)
    firstWeekStart.setDate(tomorrow.getDate() + daysUntilStart)

    // Get the Sunday of that week (for calculating other days)
    const weekStart = new Date(firstWeekStart)
    weekStart.setDate(firstWeekStart.getDate() - firstWeekStart.getDay())

    // Generate exactly 4 full weeks (or 4 fortnightly periods)
    for (let period = 0; period < 4; period++) {
      const periodStart = new Date(weekStart)
      periodStart.setDate(periodStart.getDate() + (period * frequency * 7))

      for (const targetDay of targetDayNums) {
        const targetDate = new Date(periodStart)
        targetDate.setDate(periodStart.getDate() + targetDay)

        const dateStr = this.formatDateStr(targetDate)

        // Check availability only if we have data for this date
        // If no data (future month), assume it's available
        const availability = this.availabilityData[dateStr]
        const isAvailable = !availability || availability.status === 'available'

        // Don't auto-select if all selected pets are already booked on this date
        const allPetsBooked = availability && this.areAllSelectedPetsBooked(availability.booked_pets || [])

        if (isAvailable && !allPetsBooked && !dates.includes(dateStr)) {
          dates.push(dateStr)
        }
      }
    }

    // A repeat books every pet on the same weekdays, so these days are everyone's.
    dates.sort()
    this.dayPets = {}
    this.pickingFor = null

    // Track old dates to manage phantoms
    const oldDates = new Set(this.selectedDatesValue)
    const newDates = new Set(dates)

    // Calculate which dates to add/remove
    const datesToAdd = dates.filter(d => !oldDates.has(d))
    const datesToRemove = this.selectedDatesValue.filter(d => !newDates.has(d))

    this.selectedDatesValue = dates
    this.renderCalendar()

    // Manage phantom bookings if logged in
    if (this.userLoggedInValue) {
      // Delete phantoms for removed dates
      if (datesToRemove.length > 0) {
        this.deletePhantom(datesToRemove)
      }

      // Create phantoms for added dates
      if (datesToAdd.length > 0) {
        this.createPhantomsForDates(datesToAdd)
      }
    }
  }

  // Create phantom bookings for specific dates (for all selected pets)
  async createPhantomsForDates(dates) {
    const pets = this.getSyncedPets()
    if (pets.length === 0) {
      console.warn('No synced pets available for phantom creation')
      return { success: false, error: 'No synced pets' }
    }

    for (const pet of pets) {
      await this.createPhantom(pet.prefix_id, dates)
    }
  }

  // Format date as YYYY-MM-DD string
  formatDateStr(date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  // Update the days hint text
  updateRecurringDaysHint() {
    const selectedDays = this.getSelectedRecurringDays()

    if (this.hasRecurringDaysHintTarget) {
      if (selectedDays.length > 0) {
        const dayNames = selectedDays.map(d => d.charAt(0).toUpperCase() + d.slice(1, 3))
        this.recurringDaysHintTarget.textContent = `Repeating on ${dayNames.join(', ')}`
      } else {
        this.recurringDaysHintTarget.textContent = 'Select at least one day'
      }
    }

    // Two paths set the day list — dates picked on the calendar, and these pills tapped
    // directly — and the sentence has to follow BOTH. Hooking only the first left the
    // pills changing the schedule while the explanation went on describing the old one.
    this.updateRecurringSummary(new Set(selectedDays))
  }

  // Get selected recurring days
  getSelectedRecurringDays() {
    if (!this.hasRecurringDayButtonTarget) return []

    return this.recurringDayButtonTargets
      .filter(btn => btn.classList.contains('bg-[var(--color-primary)]'))
      .map(btn => btn.dataset.day)
  }

  // Toggle end date container visibility
  toggleRecurringEndDate(event) {
    const showEndDate = event.target.value === 'on_date'

    if (this.hasRecurringEndDateContainerTarget) {
      this.recurringEndDateContainerTarget.classList.toggle('hidden', !showEndDate)
    }

    this.updateRecurringHiddenInputs()
  }

  // Handle frequency change - update dates and hidden inputs
  updateRecurringFrequency() {
    this.updateRecurringHiddenInputs()

    // Re-calculate dates based on new frequency if recurring is enabled
    if (this.hasRecurringCheckboxTarget && this.recurringCheckboxTarget.checked) {
      this.autoSelectRecurringDates()
    }
  }

  // Update hidden inputs for form submission
  // The explanation names the days back to the customer, in full and in week order, so
  // "Monday and Friday" reads as English rather than as the "Mon, Fri" of the hint above
  // it. Falls back to the server-rendered wording, which is true whatever is selected —
  // so if this never runs, the sentence still makes sense.
  updateRecurringSummary(selectedDays) {
    if (!this.hasRecurringSummaryDaysTarget && !this.hasRecurringInviteDaysTarget) return

    const ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
    const chosen = ORDER.filter(day => selectedDays.has(day))
                        .map(day => day.charAt(0).toUpperCase() + day.slice(1))

    // Each target guarded on its own: the summary and the question live in different
    // parts of the card and one can be present without the other.
    const list = chosen.length === 0
      ? null
      : chosen.length === 1
        ? chosen[0]
        : `${chosen.slice(0, -1).join(', ')} and ${chosen[chosen.length - 1]}`

    if (this.hasRecurringSummaryDaysTarget) {
      this.recurringSummaryDaysTarget.textContent = list ? `on ${list}` : "on the days you've chosen"
    }

    if (this.hasRecurringInviteDaysTarget) {
      this.recurringInviteDaysTarget.textContent = list || "the days you've chosen"
    }
  }

  updateRecurringHiddenInputs() {
    // Frequency
    if (this.hasScheduleFrequencyInputTarget && this.hasRecurringFrequencyTarget) {
      const freq = this.recurringFrequencyTarget.value
      this.scheduleFrequencyInputTarget.value = freq === 'fortnightly' ? 'bi_weekly' : 'weekly'
    }

    // Days
    if (this.hasScheduleDaysInputTarget) {
      const days = this.getSelectedRecurringDays()
      this.scheduleDaysInputTarget.value = days.join(',')
    }

    // End date
    if (this.hasScheduleEndsInputTarget && this.hasRecurringEndsTarget) {
      if (this.recurringEndsTarget.value === 'on_date' && this.hasRecurringEndDateTarget) {
        this.scheduleEndsInputTarget.value = this.recurringEndDateTarget.value
      } else {
        this.scheduleEndsInputTarget.value = ''
      }
    }
  }

  // ---- Saving the step as it changes ------------------------------------------
  // Pets and days reached the booking only on Continue, so a refresh lost both while the
  // holds the days had placed stayed behind (B94). The server keeps this as a draft beside
  // the booking, not in it; Continue still decides what is booked.

  draftPayload() {
    const petDates = {}
    this.selectedDatesValue.forEach(date => {
      this.petsForDate(date).forEach(prefixId => {
        (petDates[prefixId] ||= []).push(date)
      })
    })
    return {
      autosave: '1',
      order_item: {
        pet_ids: this.getSelectedPetIds(),
        fulfillment_data: {
          daycare_dates: this.selectedDatesValue,
          daycare_pet_dates: petDates,
          session_key: this.phantomKeyValue
        }
      }
    }
  }

  // Shortly after the last change rather than on every one: a drag changes the selection
  // once per day crossed.
  scheduleDraftSave() {
    if (!this.hasAutosaveUrlValue || !this.autosaveUrlValue) return
    this.cancelDraftSave()
    this.draftTimer = setTimeout(() => this.saveDraft(), 300)
  }

  cancelDraftSave() {
    clearTimeout(this.draftTimer)
    this.draftTimer = null
  }

  // Leaving with a save still waiting sends it anyway; keepalive lets it outlive the page.
  flushDraft() {
    if (!this.draftTimer) return
    this.cancelDraftSave()
    this.saveDraft({ keepalive: true })
  }

  async saveDraft(options = {}) {
    const payload = this.draftPayload()
    const body = JSON.stringify(payload)
    if (body === this.lastDraft) return
    this.lastDraft = body

    try {
      await fetch(this.autosaveUrlValue, {
        method: 'PATCH',
        keepalive: !!options.keepalive,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken(),
          'X-Requested-With': 'XMLHttpRequest'
        },
        body
      })
    } catch (error) {
      // Losing a draft costs a re-pick after a refresh, nothing more; Continue still posts
      // the whole selection. Tried again on the next change.
      this.lastDraft = null
      console.warn('Failed to save the days chosen so far:', error)
    }
  }

  // Override updateSummary to also update recurring state

  // "3 days selected - $225" is ambiguous once the pets are not on the same days. Spelling
  // out each pet's own days is what makes the price legible.
  renderPetBreakdown() {
    if (!this.hasPetBreakdownTarget) return

    const counts = {}
    this.selectedDatesValue.forEach(date => {
      this.petsForDate(date).forEach(prefixId => {
        counts[prefixId] = (counts[prefixId] || 0) + 1
      })
    })

    const prefixIds = Object.keys(counts)
    const uneven = prefixIds.length > 1 && new Set(Object.values(counts)).size > 1
    if (!uneven) {
      this.petBreakdownTarget.innerHTML = ''
      this.petBreakdownTarget.classList.add('hidden')
      return
    }

    this.petBreakdownTarget.innerHTML = prefixIds.map(prefixId => {
      const pet = this.petByPrefixId(prefixId)
      const days = counts[prefixId]
      return `<div class="flex justify-between"><span>${pet ? pet.name : 'Pet'}</span><span>${days} ${days === 1 ? 'day' : 'days'}</span></div>`
    }).join('')
    this.petBreakdownTarget.classList.remove('hidden')
  }

  updateSummary() {
    const daysCount = this.selectedDatesValue.length
    const petCount = this.getSelectedPetCount()
    const price = this.priceValue || 0
    // Sum the pet-days rather than multiplying: once pets are not all on the same days,
    // days x pets charges for days nobody is coming.
    const petDays = this.selectedDatesValue.reduce(
      (sum, date) => sum + this.petsForDate(date).length, 0
    )
    const total = (petDays > 0 ? petDays : daysCount * petCount) * price
    this.renderPickingFor()
    this.renderPetBreakdown()

    if (this.hasSelectedCountTarget) {
      this.selectedCountTarget.textContent = daysCount
    }

    // "Selected: 1 days" (staging UAT, 1 Oct)
    if (this.hasSelectedUnitTarget) {
      this.selectedUnitTarget.textContent = daysCount === 1 ? "day" : "days"
    }

    // Update pet count display
    if (this.hasSelectedPetCountTarget) {
      this.selectedPetCountTarget.textContent = petCount
    }
    if (this.hasPetCountTarget) {
      // Update "pet" vs "pets" pluralization
      const petLabel = petCount === 1 ? 'pet' : 'pets'
      this.petCountTarget.innerHTML = `<span data-daycare-calendar-target="selectedPetCount">${petCount}</span> ${petLabel} selected`
    }

    this.updatePetSummary()

    if (this.hasTotalPriceTarget) {
      this.totalPriceTarget.textContent = `$${total.toFixed(2)}`
    }

    if (this.hasAddButtonTarget) {
      const noPetsSelected = this.hasPetCheckboxTarget && petCount === 0
      const dayless = this.petNames(this.petsWithoutDays())
      this.addButtonTarget.disabled = daysCount === 0 || noPetsSelected || dayless.length > 0

      // Check if recurring mode is enabled
      const isRecurring = this.hasRecurringCheckboxTarget && this.recurringCheckboxTarget.checked

      let buttonText
      if (daysCount === 0) {
        buttonText = 'Select'
      } else if (noPetsSelected) {
        buttonText = 'Select a Pet'
      } else if (dayless.length > 0) {
        buttonText = `Pick days for ${this.toSentence(dayless)}`
      } else if (isRecurring) {
        buttonText = 'Create Schedule'
      } else {
        buttonText = 'Add'
      }
      this.addButtonTarget.textContent = buttonText
    }

    // Update dates container with hidden inputs for each selected date
    if (this.hasDatesContainerTarget) {
      let hiddenInputs = this.selectedDatesValue
        .map(date => `<input type="hidden" name="order_item[fulfillment_data][daycare_dates][]" value="${date}">`)
        .join('')

      // The per-pet map is what makes "Buddy only on Thu 10" survive to PTP instead of
      // being multiplied back across every pet on the item. Keyed by prefix id;
      // OrderItemsController rewrites it to Pet#id on the way in.
      const petDatesMap = {}
      this.selectedDatesValue.forEach(date => {
        this.petsForDate(date).forEach(prefixId => {
          if (!petDatesMap[prefixId]) petDatesMap[prefixId] = []
          petDatesMap[prefixId].push(date)
        })
      })
      Object.entries(petDatesMap).forEach(([prefixId, dates]) => {
        hiddenInputs += dates
          .map(date => `<input type="hidden" name="order_item[fulfillment_data][daycare_pet_dates][${prefixId}][]" value="${date}">`)
          .join('')
      })

      // Add hidden inputs for selected pet IDs
      const selectedPetIds = this.getSelectedPetIds()
      if (selectedPetIds.length > 0) {
        hiddenInputs += selectedPetIds
          .map(petId => `<input type="hidden" name="order_item[pet_ids][]" value="${petId}">`)
          .join('')
      }

      // Add session_key if logged in (for linking phantom bookings to portal order)
      if (this.userLoggedInValue && this.phantomKeyValue) {
        hiddenInputs += `<input type="hidden" name="order_item[fulfillment_data][session_key]" value="${this.phantomKeyValue}">`
      }

      this.datesContainerTarget.innerHTML = hiddenInputs
    }

    // Update the quantity field based on selected pets or manual quantity
    const quantityInput = this.element.querySelector('input[name="order_item[quantity]"]')
    if (quantityInput) {
      quantityInput.value = petCount
    }

    // Always, not only while ticked. Naming the days is what turns "would you like the
    // days you've chosen booked every week?" — a sentence about nothing — into a question
    // about Monday and Friday, and it was the one state where it never ran.
    this.updateRecurringDaysFromSelection()

    // Nothing to repeat until something is chosen.
    if (this.hasRecurringExplainerTarget) {
      this.recurringExplainerTarget.classList.toggle('hidden', this.selectedDatesValue.length === 0)
    }

    // A repeat books every pet on the same weekdays. Offering it over days chosen
    // differently per pet would set up something other than what is on screen.
    if (this.hasRecurringSectionTarget) {
      const split = this.hasChosenSplit()
      this.recurringSectionTarget.classList.toggle('hidden', split)
      if (split && this.hasRecurringCheckboxTarget && this.recurringCheckboxTarget.checked) {
        this.recurringCheckboxTarget.checked = false
        this.toggleRecurring({ target: this.recurringCheckboxTarget })
      }
    }

    this.scheduleDraftSave()
  }
}
